<script setup>
import Vue, {computed, onMounted, ref, watch} from "vue";
import {isEmpty} from "lodash";
import {usePiniaStore} from "../store/pinia_store";
import {storeToRefs} from "pinia";
import articleNumber from "../mixins/article-number";

const props = defineProps({
    item: {
        type: Object,
        required: true,
    },
    langCode: {
        type: String,
        required: true,
    },
    dentaidApiInstance: {
        type: Function,
        required: true,
    },
    cartEventBus: {
        type: Object,
        required: true,
    },
    parentEventBus: {
        type: Object,
        required: true,
    },
})

const emit = defineEmits(['favorite-selected', 'update-selected-quantity'])

const piniaStore = usePiniaStore();
const {cart} = storeToRefs(piniaStore);

const checked = ref(false);
const loading = ref(false);
const eventBus = new Vue();
const deleteProductText = window.Drupal.t('Delete product');
const quantity = ref(null);

const totalPrice = computed(() => {
    return parseFloat(props.item.translation.extra_attributes.unit_price / 100) * quantity.value;
})

const displayedArticleNumber = computed(() => articleNumber.methods.getDisplayedArticleNumber(props.item));
const articleNumberLabel = computed(() => articleNumber.methods.getArticleNumberLabel(props.item));

const handleQuantityChanged = (payload) => {
    if (checked.value) {
        emit('update-selected-quantity', {sku: props.item.sku, quantity: payload.quantity})
    }

    checked.value = true;

    quantity.value = payload.quantity;
}

const currencyFormatter = (value) => {
    return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(value)
}

const setProductQuantity = () => {
    if (!lodash.isNil(quantity.value)) {
        return;
    }

    quantity.value = props.item.min_order_quantity;
}

watch(checked, () => {
    emit('favorite-selected', {sku: props.item.sku, quantity: quantity.value})
})

const handleRemove = () => {
    if (confirm('Are you sure?')) {
        checked.value = false;
        loading.value = true;

        props.dentaidApiInstance.post(`/favorites/delete?token=${piniaStore.parsedToken}`, {
            items: {
                "0": {
                    sku: props.item.sku
                }
            }
        }).then(response => {
            piniaStore.updateUserData(response.data.data);

            Vue.$toast.success(window.Drupal.t("Successfully removed product from favorites"))
        }).catch(error => {
            Vue.$toast.error(window.Drupal.t("Something went wrong, please try again later."))
        }).finally((response) => {
            loading.value = false
        });
    }
}

watch(() => piniaStore.isLoading, () => {
    if (!isEmpty(cart.value.lines?.[props.item.sku] ?? null) && !piniaStore.isLoading) {
        quantity.value = cart.value.lines[props.item.sku].quantity;
    }
}, {deep: true})

onMounted(() => {
    setProductQuantity();

    setTimeout(() => {
        eventBus.$on('quantity-changed', (payload) => handleQuantityChanged(payload));
    }, 1000)

    props.parentEventBus.$on('reset-checked-favorites', () => checked.value = false);
})
</script>

<template>
    <div class="vue-favorite-line">
        <div class="d-flex flex-wrap align-items-start align-items-sm-center">
            <div class="form-type-checkbox">
                <input type="checkbox" v-model="checked" :name="`check-favorite-${item.sku}`">
            </div>

            <div class="col-2 col-sm-3 col-md-4 col-xxl-2 px-0 px-md-2">
                <img :src="item.translation.image" :alt="item.translation.name">
            </div>

            <div class="col-7 col-sm-8 col-md-6 col-xxl-3">
                <h5 class="item-title">{{ item.translation.name }}</h5>

                <span class="meta-info">{{ articleNumberLabel }} {{
                        displayedArticleNumber
                    }} - {{ item.translation.extra_attributes.volume }}</span>

                <button class="delete-from-cart" :disabled="loading" @click="handleRemove">{{
                        deleteProductText
                    }}
                </button>
            </div>

            <div class="col-5 col-xxl-3 mt-5 mt-xxl-0 offset-sm-1 offset-xxl-0 px-2 px-xl-3">
                <dentaid-product-quantity
                    :event-bus="eventBus"
                    :crm-id="item.sku"
                    :incremental-order-quantity="item.incremental_order_quantity"
                    :minimum-order-quantity="item.min_order_quantity"
                    :cart-event-bus="cartEventBus"
                ></dentaid-product-quantity>
            </div>

            <div class="col-3 col-xxl-2 mt-5 mt-xxl-0">
                {{ currencyFormatter(item.translation.extra_attributes.unit_price / 100) }}
            </div>

            <div class="col-3 col-xxl-1 mt-5 mt-xxl-0">
                <span class="font-weight-bold">
                    {{ currencyFormatter(totalPrice) }}
                </span>
            </div>
        </div>
    </div>
</template>